import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';
import { getQuickActions } from '../../data/mockApi';
import { FiX, FiFileText, FiPlus, FiSend, FiDollarSign, FiPackage } from 'react-icons/fi';

// Dark theme action colors
const ACTION_COLORS = {
  emerald: {
    bg: 'hover:bg-lime-500/10',
    border: 'hover:border-lime-500/30',
    icon: 'text-lime-400 group-hover:text-lime-300'
  },
  blue: {
    bg: 'hover:bg-sky-500/10',
    border: 'hover:border-sky-500/30',
    icon: 'text-sky-400 group-hover:text-sky-300'
  },
  purple: {
    bg: 'hover:bg-violet-500/10',
    border: 'hover:border-violet-500/30',
    icon: 'text-violet-400 group-hover:text-violet-300'
  },
  amber: {
    bg: 'hover:bg-amber-500/10',
    border: 'hover:border-amber-500/30',
    icon: 'text-amber-400 group-hover:text-amber-300'
  },
  rose: {
    bg: 'hover:bg-rose-500/10',
    border: 'hover:border-rose-500/30',
    icon: 'text-rose-400 group-hover:text-rose-300'
  },
  cyan: {
    bg: 'hover:bg-cyan-500/10',
    border: 'hover:border-cyan-500/30',
    icon: 'text-cyan-400 group-hover:text-cyan-300'
  }
};

// Modal content components for each action type
const RFQFormModal = ({ onClose }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Supplier</label>
      <select className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500">
        <option value="">Select a supplier</option>
        <option value="farm-fresh">Farm Fresh Suppliers</option>
        <option value="green-valley">Green Valley Produce</option>
        <option value="organic-farms">Organic Farms Ltd</option>
      </select>
    </div>
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Product Category</label>
      <select className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500">
        <option value="">Select category</option>
        <option value="fruits">Fruits</option>
        <option value="vegetables">Vegetables</option>
        <option value="herbs">Herbs</option>
        <option value="exotic">Exotic Produce</option>
      </select>
    </div>
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Required Quantity</label>
      <input type="number" placeholder="Enter quantity" className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500" />
    </div>
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Delivery Date Required</label>
      <input type="date" className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500" />
    </div>
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Notes</label>
      <textarea rows={3} placeholder="Additional requirements..." className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500 resize-none" />
    </div>
    <div className="flex gap-3 pt-2">
      <button onClick={onClose} className="flex-1 bg-[#1c2822] border border-[#243129] text-[#e8f5e9] py-2 rounded-lg font-mono text-sm hover:bg-[#243129] transition-colors">Cancel</button>
      <button onClick={() => { toast.success('RFQ submitted successfully'); onClose(); }} className="flex-1 bg-gradient-to-r from-lime-500 to-lime-600 text-[#050807] py-2 rounded-lg font-mono text-sm font-medium hover:from-lime-400 hover:to-lime-500 transition-all">Submit RFQ</button>
    </div>
  </div>
);

const CreateOrderModal = ({ onClose }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Customer</label>
      <select className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500">
        <option value="">Select customer</option>
        <option value="fresh-market">Fresh Market Retailers</option>
        <option value="organic-grocer">The Organic Grocer</option>
        <option value="city-supermarket">City Supermarket Chain</option>
      </select>
    </div>
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Product</label>
      <select className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500">
        <option value="">Select product</option>
        <option value="apples">Granny Smith Apples</option>
        <option value="oranges">Navel Oranges</option>
        <option value="bananas">Cavendish Bananas</option>
        <option value="tomatoes">Beef Tomatoes</option>
      </select>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="block text-sm font-mono text-[#6b8772] mb-1">Quantity</label>
        <input type="number" placeholder="0" className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500" />
      </div>
      <div>
        <label className="block text-sm font-mono text-[#6b8772] mb-1">Unit Price</label>
        <input type="number" step="0.01" placeholder="N$ 0.00" className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500" />
      </div>
    </div>
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Delivery Address</label>
      <input type="text" placeholder="Enter delivery address" className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500" />
    </div>
    <div className="flex gap-3 pt-2">
      <button onClick={onClose} className="flex-1 bg-[#1c2822] border border-[#243129] text-[#e8f5e9] py-2 rounded-lg font-mono text-sm hover:bg-[#243129] transition-colors">Cancel</button>
      <button onClick={() => { toast.success('Order created successfully'); onClose(); }} className="flex-1 bg-gradient-to-r from-lime-500 to-lime-600 text-[#050807] py-2 rounded-lg font-mono text-sm font-medium hover:from-lime-400 hover:to-lime-500 transition-all">Create Order</button>
    </div>
  </div>
);

const SendProposalModal = ({ onClose }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Customer</label>
      <select className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500">
        <option value="">Select customer</option>
        <option value="fresh-market">Fresh Market Retailers</option>
        <option value="organic-grocer">The Organic Grocer</option>
        <option value="city-supermarket">City Supermarket Chain</option>
      </select>
    </div>
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Proposal Title</label>
      <input type="text" placeholder="E.g., Q1 Fresh Produce Supply" className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500" />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="block text-sm font-mono text-[#6b8772] mb-1">Total Value (N$)</label>
        <input type="number" placeholder="0.00" className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500" />
      </div>
      <div>
        <label className="block text-sm font-mono text-[#6b8772] mb-1">Valid Until</label>
        <input type="date" className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500" />
      </div>
    </div>
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Proposal Details</label>
      <textarea rows={3} placeholder="Describe the proposal terms..." className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500 resize-none" />
    </div>
    <div className="flex gap-3 pt-2">
      <button onClick={onClose} className="flex-1 bg-[#1c2822] border border-[#243129] text-[#e8f5e9] py-2 rounded-lg font-mono text-sm hover:bg-[#243129] transition-colors">Cancel</button>
      <button onClick={() => { toast.success('Proposal sent successfully'); onClose(); }} className="flex-1 bg-gradient-to-r from-violet-500 to-violet-600 text-white py-2 rounded-lg font-mono text-sm font-medium hover:from-violet-400 hover:to-violet-500 transition-all">Send Proposal</button>
    </div>
  </div>
);

const GenerateInvoiceModal = ({ onClose }) => {
  const pendingOrders = [
    { id: 'ORD-2024-0156', customer: 'Fresh Market Retailers', amount: 45750, date: '2024-01-15' },
    { id: 'ORD-2024-0157', customer: 'The Organic Grocer', amount: 32400, date: '2024-01-15' },
    { id: 'ORD-2024-0158', customer: 'City Supermarket', amount: 67890, date: '2024-01-14' }
  ];
  return (
    <div className="space-y-4">
      <p className="text-sm font-mono text-[#6b8772]">Select an order to generate invoice:</p>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {pendingOrders.map((order) => (
          <button
            key={order.id}
            onClick={() => { toast.success(`Invoice generated for ${order.id}`); onClose(); }}
            className="w-full p-3 bg-[#0a0f0d] border border-[#243129] rounded-lg text-left hover:border-lime-500/50 hover:bg-lime-500/5 transition-all group"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="font-mono text-sm font-medium text-[#e8f5e9] group-hover:text-lime-400">{order.id}</p>
                <p className="font-mono text-xs text-[#6b8772] mt-1">{order.customer}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-sm font-medium text-lime-400">N$ {order.amount.toLocaleString()}</p>
                <p className="font-mono text-xs text-[#4a5f50] mt-1">{order.date}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
      <button onClick={onClose} className="w-full bg-[#1c2822] border border-[#243129] text-[#e8f5e9] py-2 rounded-lg font-mono text-sm hover:bg-[#243129] transition-colors">Cancel</button>
    </div>
  );
};

const UpdateStockModal = ({ onClose }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Product</label>
      <select className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500">
        <option value="">Select product</option>
        <option value="APL-001">APL-001 - Granny Smith Apples</option>
        <option value="ORG-023">ORG-023 - Navel Oranges</option>
        <option value="BAN-012">BAN-012 - Cavendish Bananas</option>
        <option value="TOM-045">TOM-045 - Beef Tomatoes</option>
      </select>
    </div>
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Adjustment Type</label>
      <select className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500">
        <option value="add">Add Stock (+)</option>
        <option value="remove">Remove Stock (-)</option>
        <option value="set">Set Exact Count</option>
      </select>
    </div>
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Quantity</label>
      <input type="number" placeholder="Enter quantity" className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500" />
    </div>
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Reason</label>
      <select className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500">
        <option value="">Select reason</option>
        <option value="received">Stock Received</option>
        <option value="damaged">Damaged/Spoiled</option>
        <option value="count">Physical Count Adjustment</option>
        <option value="returned">Customer Return</option>
      </select>
    </div>
    <div>
      <label className="block text-sm font-mono text-[#6b8772] mb-1">Notes</label>
      <input type="text" placeholder="Optional notes" className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500" />
    </div>
    <div className="flex gap-3 pt-2">
      <button onClick={onClose} className="flex-1 bg-[#1c2822] border border-[#243129] text-[#e8f5e9] py-2 rounded-lg font-mono text-sm hover:bg-[#243129] transition-colors">Cancel</button>
      <button onClick={() => { toast.success('Stock updated successfully'); onClose(); }} className="flex-1 bg-gradient-to-r from-lime-500 to-lime-600 text-[#050807] py-2 rounded-lg font-mono text-sm font-medium hover:from-lime-400 hover:to-lime-500 transition-all">Update Stock</button>
    </div>
  </div>
);

const QuickActions = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const data = await getQuickActions();
      setActions(data);
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleActionClick = (actionId, title) => {
    // Open the appropriate modal based on action id
    setActiveModal(actionId);
  };

  const closeModal = () => setActiveModal(null);

  const getModalContent = () => {
    switch (activeModal) {
      case 'rfq':
        return { title: 'Initiate RFQ', icon: FiFileText, component: <RFQFormModal onClose={closeModal} /> };
      case 'order':
        return { title: 'Create Order', icon: FiPlus, component: <CreateOrderModal onClose={closeModal} /> };
      case 'proposal':
        return { title: 'Send Proposal', icon: FiSend, component: <SendProposalModal onClose={closeModal} /> };
      case 'invoice':
        return { title: 'Generate Invoice', icon: FiDollarSign, component: <GenerateInvoiceModal onClose={closeModal} /> };
      case 'stock':
        return { title: 'Update Stock', icon: FiPackage, component: <UpdateStockModal onClose={closeModal} /> };
      default:
        return null;
    }
  };

  const getActionColors = (color) => ACTION_COLORS[color] || ACTION_COLORS.emerald;

  return (
    <motion.div
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.1, duration: 0.4 }}
      className="bg-[#111916] rounded-xl border border-[#243129]"
    >
      <div className="p-5 border-b border-[#243129]">
        <h3 className="font-display text-lg font-semibold text-[#e8f5e9]">Quick Actions</h3>
        <p className="text-xs font-mono text-[#6b8772] mt-1">Streamlined workflow triggers</p>
      </div>
      <div className="p-5">
        {loading ? (
          <div className="text-center text-[#6b8772] font-mono text-sm py-4">Loading actions...</div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {actions.map((action, index) => {
              const colors = getActionColors(action.color);
              return (
                <motion.button
                  key={action.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.03 }}
                  whileHover={{ y: -2, transition: { duration: 0.15 } }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => handleActionClick(action.id, action.title)}
                  className={`group p-3 rounded-lg bg-[#0a0f0d] border border-[#1c2822] ${colors.bg} ${colors.border} transition-all duration-200 text-left`}
                >
                  <SafeIcon icon={action.icon} className={`w-5 h-5 mb-2 ${colors.icon} transition-colors`} />
                  <h4 className="font-mono font-medium text-sm text-[#e8f5e9] group-hover:text-white transition-colors">{action.title}</h4>
                  <p className="text-[10px] font-mono text-[#4a5f50] mt-0.5 line-clamp-1">{action.description}</p>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal Overlay */}
      <AnimatePresence>
        {activeModal && getModalContent() && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-[#111916] border border-[#243129] rounded-xl w-full max-w-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-5 border-b border-[#243129] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-lime-500/10 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={getModalContent().icon} className="w-5 h-5 text-lime-400" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-[#e8f5e9]">{getModalContent().title}</h3>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-[#243129] rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5 text-[#6b8772]" />
                </button>
              </div>
              {/* Modal Content */}
              <div className="p-5">
                {getModalContent().component}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default QuickActions;
